import { computed, DestroyRef, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TaskCreateData } from '@app/components/task/task-create/task-create.component';
import { TasksHttpService } from '@app/shared/services/http/tasks-http.service';
import { TaskSortField } from '@app/shared/types/enums/task-sort-field';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { catchError, concat, EMPTY, finalize, last, Observable, throwError, timeout } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { AppEventType } from '../types/enums/app-event-type';
import { SortDirection } from '../types/enums/sort-direction.enum';
import { Location } from '../types/models/location/location.model';
import { AppEvent } from '../types/models/notifications/app-event';
import { TaskContractCost } from '../types/models/task/task-contract-cost';
import { TaskDescriptionComment } from '../types/models/task/task-description-comment.model';
import { TaskEstimation } from '../types/models/task/task-estimation';
import { TaskFilters } from '../types/models/task/task-filters.model';
import { TaskFinalCost } from '../types/models/task/task-final-cost';
import { TaskType } from '../types/models/task/task-type.model';
import { Task } from '../types/models/task/task.model';
import { TasksPageData } from '../types/models/tasks-page-data';
import { buildTaskEstimationLikePatchPlan, TaskEstimationLikeValues } from '../utils/task-estimation-like.utils';
import { normalizeTaskFilters } from '../utils/task-filters.util';
import { CompaniesDataService } from './companies-data.service';
import { CompanyIndustriesDataService } from './company-industries-data.service';
import { DataService } from './data-service';
import { ContextDataDTO } from './dtos/context-data.dto';
import { ModelWithContextData } from './dtos/dto-with-context-data.dto';
import { PaginatedDataDTO } from './dtos/paginated-data.dto';
import { CopyTaskToProjectDTO } from './dtos/project-tasks/copy-task-to-project.dto';
import { ProjectTaskDTO } from './dtos/project-tasks/project-task.dto';
import { ProjectTaskFactory } from './factories/project-task.factory';
import { GroupsDataService } from './groups-data.service';
import { LocationsDataService } from './locations-data.service';
import { ProjectIndustriesDataService } from './project-industries-data.service';
import { ProjectParticipantsDataService } from './project-participants-data.service';
import { ProjectsDataService } from './projects-data.service';
import { TaskParticipantsDataService } from './task-participants-data.service';
import { TaskTypesDataService } from './task-types-data.service';
import { UsersDataService } from './users-data.service';

export interface PageKey {
  projectId: string;
  page: number;
  limit: number;
  sortField: string;
  sortDir: string;
  filtersId: string;
  searchText?: string;
  filters?: TaskFilters;
}

export const getPageKey = (key: PageKey, filtersHash = ''): string =>
  `${key.projectId}-${key.page}-${key.limit}-${convertToKeyString(key.sortField)}-${convertToKeyString(key.sortDir)}-${convertToKeyString(key.filtersId)}-${convertToKeyString(key.searchText || '')}-${filtersHash}`;

export const convertToKeyString = (value: string): string => (value ? value.trim().toLowerCase() : '');

/**
 * TasksDataService - Data access layer for tasks.
 *
 * This service manages task data, queries, store operations and provides getters for task state.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link TasksActionsService} and components to access and manage task data.
 *
 * Architecture:
 * - {@link TasksActionsService}: User actions, mutations, side effects (toasts, modals)
 * - {@link TasksDataService}: Data access, queries, getters, store management
 * - {@link TasksHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class TasksDataService extends DataService<ProjectTaskDTO, Task> {
  private readonly tasks: DataStore<Task> = new DataStore<Task>();
  private readonly projectTaskPages: DataStore<TasksPageData> = new DataStore<TasksPageData>();
  private readonly tasksByParent: DataStore<IdsCollection> = new DataStore<IdsCollection>();
  private readonly childrenLoadStates = signal<Record<string, 'loading' | 'loaded' | 'error'>>({});
  private readonly activeChildScans = new Map<string, { projectId: string }>();
  private readonly taskFactory: ProjectTaskFactory = inject(ProjectTaskFactory);
  private readonly companiesService: CompaniesDataService = inject(CompaniesDataService);
  private readonly groupsService: GroupsDataService = inject(GroupsDataService);
  private readonly industriesService: CompanyIndustriesDataService = inject(CompanyIndustriesDataService);
  private readonly locationsService: LocationsDataService = inject(LocationsDataService);
  private readonly projectsService: ProjectsDataService = inject(ProjectsDataService);
  private readonly projectIndustriesService: ProjectIndustriesDataService = inject(ProjectIndustriesDataService);
  private readonly projectParticipantsService: ProjectParticipantsDataService = inject(ProjectParticipantsDataService);
  private readonly tasksHttpService: TasksHttpService = inject(TasksHttpService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly taskTypesService: TaskTypesDataService = inject(TaskTypesDataService);
  private readonly usersService: UsersDataService = inject(UsersDataService);
  private readonly taskParticipantsService: TaskParticipantsDataService = inject(TaskParticipantsDataService);
  private currentPageKey: PageKey;
  private inflight = new Set<string>();
  /** Last rendered task page — shown while a new filtered page is loading to avoid layout jumps. */
  private lastProjectTasksPageData: TasksPageData | undefined;

  public getById(taskId: string): Signal<Task | undefined> {
    if (!this.tasks.hasDataForId(taskId)) {
      this.fetchTaskById(taskId);
    }

    return computed(() => this.tasks.get(taskId)());
  }

  /**
   * Re-fetches a task from the API even when it is already cached, so that server-computed
   * fields (statistics, timer totals) reflect changes made through other endpoints.
   */
  public refreshTask(taskId: string): void {
    if (!taskId) {
      return;
    }

    this.fetchTaskById(taskId);
  }

  public getProjectTasks(
    projectId: string,
    page: number,
    limit: number,
    sortField: TaskSortField,
    sortDir: SortDirection,
    filtersId?: string,
    filters?: TaskFilters
  ): Signal<TasksPageData | undefined> {
    if (!projectId) return signal(undefined);

    const normalized: TaskFilters = { ...((filters ?? {}) as TaskFilters) };
    delete (normalized as Record<string, unknown>)['direction'];
    delete (normalized as Record<string, unknown>)['limit'];
    delete (normalized as Record<string, unknown>)['page'];

    const effectiveFilters = this.isObjectEmpty(normalized) ? null : normalized;

    const key: PageKey = {
      projectId,
      page,
      limit,
      sortField,
      sortDir,
      filtersId: filtersId ?? '',
      searchText: effectiveFilters?.text ?? '',
      filters: effectiveFilters
    };

    this.currentPageKey = key;

    this.ensureTasksPageLoaded(key);

    const pageKey = this.resolvePageKey(key);

    return computed(() => {
      const pageData = this.projectTaskPages.get(pageKey)();
      if (pageData) {
        this.lastProjectTasksPageData = this.mapStoredPageData(pageData);
        return this.lastProjectTasksPageData;
      }

      if (this.inflight.has(pageKey) && this.lastProjectTasksPageData) {
        return this.lastProjectTasksPageData;
      }

      return undefined;
    });
  }

  private resolvePageKey(key: PageKey): string {
    return getPageKey(key, this.buildFiltersHash(key.filters));
  }

  private mapStoredPageData(pageData: TasksPageData): TasksPageData {
    let filters = null;
    if (pageData.filters) {
      filters = structuredClone(pageData.filters);
      delete (filters as TaskFilters)['direction'];
      delete (filters as TaskFilters)['limit'];
      delete (filters as TaskFilters)['page'];
    }

    return {
      total: pageData.total,
      id: pageData.id,
      filtersId: pageData.filtersId ?? null,
      filters,
      page: pageData.page,
      limit: pageData.limit,
      content: pageData.ids.map(id => this.tasks.get(id))
    } as TasksPageData;
  }

  public getTasksByParent(projectId: string, taskId: string): Signal<Task[] | undefined> {
    if (!projectId || !taskId) {
      return signal([]);
    }

    if (!this.tasksByParent.hasDataForId(taskId)) {
      untracked(() => this.fetchTasksByParent(projectId, taskId));
    }

    return computed(() => {
      const childrenIds = this.tasksByParent.get(taskId)();
      return childrenIds?.ids.map(id => this.tasks.get(id)()) || [];
    });
  }

  /** Loading state for one parent's direct children, shared with the existing cache. */
  public getTasksByParentResult(
    projectId: string,
    taskId: string
  ): Signal<{
    status: 'loading' | 'loaded' | 'error';
    tasks: Task[];
  }> {
    const tasks = this.getTasksByParent(projectId, taskId);
    return computed(() => ({
      status: this.childrenLoadStates()[taskId] ?? 'loaded',
      tasks: tasks() ?? []
    }));
  }

  protected handleEvent(event: AppEvent): void {
    // The base constructor replays the last notification before this class has a cache to update.
    if (!this.tasks) return;

    if (event.eventType === 'TaskUpdated') {
      this.tasks.upsert(event.data as Task);
    }

    if (event.eventType === 'TaskDescriptionCommentAdded' || event.eventType === 'TaskDescriptionCommentUpdated') {
      this.fetchTaskById((event.data as { taskId: string; comment: TaskDescriptionComment }).taskId);
    }
  }

  public upsertLocalData(dto: Partial<ProjectTaskDTO>): Signal<Task> {
    if (!dto) {
      return signal(undefined);
    }

    return this.tasks.upsert(this.taskFactory.produce(this.mergePartialTaskDto(dto)));
  }

  private mergePartialTaskDto(dto: Partial<ProjectTaskDTO>): Partial<ProjectTaskDTO> {
    if (!dto.id) {
      return dto;
    }

    const existingTask = this.tasks.get(dto.id)?.();
    if (!existingTask) {
      return dto;
    }

    return {
      ...dto,
      projectId: dto.projectId ?? existingTask.project()?.id,
      groupId: dto.groupId ?? existingTask.projectGroupAdministrator()?.group()?.id,
      industryId: dto.industryId ?? existingTask.projectIndustry()?.industry()?.id
    };
  }

  private fetchTaskById(taskId: string): void {
    this.tasksHttpService
      .getTask(taskId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (dto: ProjectTaskDTO) => {
          if (dto) {
            const task = this.taskFactory.produce(dto);
            if (task) {
              this.tasks.upsert(task);
            }
          }
        }
      });
  }

  private fetchTasksPage(pageKey: PageKey, onFinally?: () => void): void {
    this.tasksHttpService
      .fetchTasks(pageKey)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => onFinally?.())
      )
      .subscribe({
        next: response => {
          if (response.contextData) this.uploadContextDataItems(response.contextData);

          const taskItems = response.data.items.map(projectTaskDto => this.taskFactory.produce(projectTaskDto));
          this.tasks.upsertMany(taskItems);

          const stringPageKey = this.resolvePageKey(pageKey);

          this.projectTaskPages.upsert({
            id: stringPageKey,
            ids: response.data.items.map(itm => itm.id),
            filters: normalizeTaskFilters(response.data?.appliedFilter?.state ?? null),
            filtersId: response.data?.appliedFilter?.filterRef?.id ?? null,
            total: response.data.total,
            limit: response.data.limit,
            page: response.data.page
          });
        },
        error: () => {}
      });
  }

  private fetchTasksByParent(projectId: string, taskId: string, replacePending = false): void {
    if (this.childrenLoadStates()[taskId] === 'loading' && !replacePending) return;
    const scan = { projectId };
    this.activeChildScans.set(taskId, scan);
    const isCurrentScan = (): boolean => this.activeChildScans.get(taskId) === scan;
    const setStatus = (status: 'loading' | 'loaded' | 'error'): void => {
      this.childrenLoadStates.update(states => ({ ...states, [taskId]: status }));
    };
    setStatus('loading');
    const ids = new Set<string>();
    const fetchPage = (page: number): void => {
      this.tasksHttpService
        .fetchSubtasks(projectId, taskId, {
          pageRequest: { page, limit: 1000, sortByField: 'createdAt', direction: SortDirection.ASC }
        })
        .pipe(timeout(30000), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: ModelWithContextData<PaginatedDataDTO<ProjectTaskDTO>>) => {
            if (!isCurrentScan()) return;
            if (response.contextData) this.uploadContextDataItems(response.contextData);
            const previousSize = ids.size;
            response.data.items.forEach(dto => {
              this.upsertLocalData(dto);
              ids.add(dto.id);
            });
            if (ids.size < response.data.total) {
              // Stop if the endpoint repeats a page or cannot make progress.
              if (ids.size === previousSize) {
                this.activeChildScans.delete(taskId);
                setStatus('error');
                return;
              }
              fetchPage(page + 1);
              return;
            }
            this.tasksByParent.upsert({ id: taskId, ids: [...ids] });
            this.activeChildScans.delete(taskId);
            setStatus('loaded');
          },
          error: () => {
            if (!isCurrentScan()) return;
            this.activeChildScans.delete(taskId);
            setStatus('error');
          }
        });
    };
    fetchPage(0);
  }

  public uploadContextDataItems(contextData: ContextDataDTO): void {
    if (!contextData) {
      return;
    }

    contextData.companies?.forEach(dto => this.companiesService.upsertLocalData(dto));
    contextData.groups?.forEach(dto => this.groupsService.upsertLocalData(dto));
    contextData.industries?.forEach(dto => this.industriesService.upsertLocalData(dto));
    contextData.locations?.forEach(dto => this.locationsService.upsertLocalData(dto));
    contextData.projects?.forEach(dto => this.projectsService.upsertLocalData(dto));
    contextData.projectIndustries?.forEach(dto => this.projectIndustriesService.upsertLocalData(dto));
    contextData.projectParticipants?.forEach(dto => this.projectParticipantsService.upsertLocalData(dto));
    contextData.projectTaskParticipants?.forEach(dto => {
      this.taskParticipantsService.upsertLocalData(dto);
      this.taskParticipantsService.upsertLocalDataTaskParticipants(dto);
    });
    contextData.tasks?.forEach(dto => this.upsertLocalData(dto));
    contextData.taskTypes?.forEach(dto => this.taskTypesService.upsertLocalData(dto));
    contextData.users?.forEach(dto => this.usersService.upsertLocalData(dto));
  }

  public createNewTask(projectId: string, request: TaskCreateData): Observable<ProjectTaskDTO[]> {
    return this.tasksHttpService.createTask(projectId, request).pipe(
      tap((res: ProjectTaskDTO[]) => {
        const newTask = res[0];
        this.upsertLocalData(newTask);
        this.fetchTasksPage(this.currentPageKey);
        if (newTask.parentTaskId) {
          this.changeTaskSubtasksCount('add', newTask.parentTaskId, newTask.id);
        }
        this.notificationService.notify({ eventType: 'TaskCreated', data: newTask });
      }),
      catchError(() => throwError(() => request))
    );
  }

  public copyTaskToProject(taskId: string, payload: CopyTaskToProjectDTO): Observable<Task> {
    return this.tasksHttpService.copyTaskToProject(taskId, payload).pipe(
      tap((dto: ProjectTaskDTO) => {
        if (dto.parentTaskId) this.changeTaskSubtasksCount('add', dto.parentTaskId, dto.id);
      }),
      map((dto: ProjectTaskDTO) => this.taskFactory.produce(dto)),
      tap((task: Task) => {
        this.tasks.upsert(task);
        this.fetchTasksPage(this.currentPageKey);
        this.notificationService.notify({ eventType: 'TaskCreated', data: task });
      })
    );
  }

  public deleteTask(task: Task): Observable<ProjectTaskDTO> {
    return this.tasksHttpService.deleteTask(task.id).pipe(
      tap((dto: ProjectTaskDTO) => {
        const taskId = task.id;
        if (taskId) {
          this.activeChildScans.delete(taskId);
          this.childrenLoadStates.update(states => {
            const remaining = { ...states };
            delete remaining[taskId];
            return remaining;
          });
        }
        this.tasks.delete(task.id);
        this.tasksByParent.delete(task.id);
        this.fetchTasksPage(this.currentPageKey);
        const parentTaskId = dto?.parentTaskId === null ? null : (dto?.parentTaskId ?? task.parentTask()?.id);
        if (parentTaskId) this.changeTaskSubtasksCount('delete', parentTaskId, task.id);
      })
    );
  }

  public updateTaskType(task: Task, taskType: TaskType): Observable<ProjectTaskDTO> {
    if (!taskType) return;
    return this.updateTaskField(task, 'type', { code: taskType.code });
  }

  public updateTaskName(task: Task, name: string): Observable<ProjectTaskDTO> {
    if (!name) return;
    return this.updateTaskField(task, 'name', { name });
  }

  public updateTaskDescription(
    task: Task,
    description: string,
    requireConfirmationAgain?: boolean
  ): Observable<ProjectTaskDTO> {
    if (!description) return;
    const payload =
      requireConfirmationAgain === undefined ? { description } : { description, requireConfirmationAgain };
    return this.updateTaskField(task, 'description', payload, 'TaskDataChanged');
  }

  public updateTaskStatus(task: Task, status: string): Observable<ProjectTaskDTO> {
    if (!status) return;
    return this.updateTaskField(task, 'status', { status }, 'TaskDataChanged');
  }

  public updateTaskPriority(task: Task, priority: string): Observable<ProjectTaskDTO> {
    if (!priority) return;
    return this.updateTaskField(task, 'priority', { priority }, 'TaskDataChanged');
  }

  public updateCompletionPercentage(percentage: number, task: Task): Observable<ProjectTaskDTO> {
    if (percentage < 0 || percentage > 100) return;
    return this.updateTaskField(task, 'progress', { percentageOfProgress: percentage }, 'TaskDataChanged');
  }

  public updateStartDate(startDate: Date, task: Task): Observable<ProjectTaskDTO> {
    if (!startDate) return;
    return this.updateTaskField(task, 'start-date', { startDate: startDate.toISOString() });
  }

  public updateEndDate(endDate: Date, task: Task): Observable<ProjectTaskDTO> {
    if (!endDate) return;
    return this.updateTaskField(task, 'end-date', { endDate: endDate.toISOString() });
  }

  public updateEstimation(estimation: TaskEstimation, task: Task): Observable<ProjectTaskDTO> {
    return this.patchEstimationLikeFields(task, 'estimation', estimation, task.estimation);
  }

  public updateFinalCost(finalCost: TaskFinalCost, task: Task): Observable<ProjectTaskDTO> {
    return this.patchEstimationLikeFields(task, 'final-cost', finalCost, task.finalCost);
  }

  public updateContractCost(contractCost: TaskContractCost, task: Task): Observable<ProjectTaskDTO> {
    return this.patchEstimationLikeFields(task, 'contract-cost', contractCost, task.contractCost);
  }

  private patchEstimationLikeFields(
    task: Task,
    endpointPrefix: 'estimation' | 'final-cost' | 'contract-cost',
    updated: TaskEstimationLikeValues,
    current?: TaskEstimationLikeValues
  ): Observable<ProjectTaskDTO> {
    const patchPlan = buildTaskEstimationLikePatchPlan(current, updated);
    const patchRequests: Observable<ProjectTaskDTO>[] = [];

    if (patchPlan.time) {
      patchRequests.push(this.updateTaskField(task, `${endpointPrefix}/time`, patchPlan.time));
    }

    if (patchPlan.financial) {
      patchRequests.push(this.updateTaskField(task, `${endpointPrefix}/finances`, patchPlan.financial));
    }

    if (patchPlan.quantity) {
      patchRequests.push(this.updateTaskField(task, `${endpointPrefix}/quantity`, patchPlan.quantity));
    }

    if (patchRequests.length === 0) {
      return EMPTY;
    }

    return concat(...patchRequests).pipe(last());
  }

  public updateIndustry(industryId: string, task: Task): Observable<ProjectTaskDTO> {
    if (!industryId) return EMPTY;
    return this.updateTaskField(task, 'industry', { industryId: industryId }, 'TaskDataChanged').pipe(
      tap(() => this.taskParticipantsService.reloadTaskParticipants(task.id))
    );
  }

  public updateLocation(location: Location, task: Task): Observable<ProjectTaskDTO> {
    if (!location) return EMPTY;
    return this.updateTaskField(task, 'location', { locationId: location.id }, 'TaskDataChanged');
  }

  public updateTaskGroup(groupId: string, task: Task): Observable<ProjectTaskDTO> {
    if (!groupId) return EMPTY;
    return this.updateTaskField(task, 'group', { groupId: groupId }, 'TaskGroupChanged');
  }

  public updateAvatarId(avatarId: string, task: Task): Observable<ProjectTaskDTO> {
    if (!avatarId) return EMPTY;
    return this.updateTaskField(task, 'avatar', { avatarId: avatarId }, 'TaskDataChanged');
  }

  public updateTaskParent(task: Task, parentTaskId: string | null): Observable<ProjectTaskDTO> {
    if (!task) return EMPTY;
    const oldParentId = task.parentTask()?.id ?? null;
    return this.tasksHttpService.changeParent(task, parentTaskId).pipe(
      tap((dto: ProjectTaskDTO) => {
        this.upsertLocalData(dto);
        if (oldParentId !== parentTaskId) {
          if (oldParentId) this.changeTaskSubtasksCount('delete', oldParentId, task.id);
          if (parentTaskId) this.changeTaskSubtasksCount('add', parentTaskId, task.id);
        }
        if (this.currentPageKey) this.fetchTasksPage(this.currentPageKey);
      })
    );
  }

  public updateTaskAcknowledgementRequested(
    task: Task,
    acknowledgementRequested: boolean | null
  ): Observable<ProjectTaskDTO> {
    if (!task?.id) return EMPTY;
    return this.updateTaskField(task, 'acknowledgement-requested', { acknowledgementRequested }, 'TaskDataChanged');
  }

  private updateTaskField<T>(
    task: Task,
    endpoint: string,
    payload: T,
    notify?: AppEventType
  ): Observable<ProjectTaskDTO> {
    if (!task) return;

    return this.tasksHttpService.updateField(task, endpoint, payload).pipe(
      tap((dto: ProjectTaskDTO) => {
        this.upsertLocalData(dto);
        if (notify) this.notificationService.notify({ eventType: notify, data: task.id });
      })
    );
  }

  private changeTaskSubtasksCount(method: 'add' | 'delete', parentTaskId: string, subtaskId: string): void {
    const hasLoadedChildren = this.tasksByParent.hasDataForId(parentTaskId);
    let membershipChanged = true;

    if (hasLoadedChildren) {
      const existingIds = this.tasksByParent.get(parentTaskId)()?.ids ?? [];
      const containsSubtask = existingIds.includes(subtaskId);
      membershipChanged = method === 'add' ? !containsSubtask : containsSubtask;

      if (membershipChanged) {
        const updatedIds = method === 'add' ? [...existingIds, subtaskId] : existingIds.filter(id => id !== subtaskId);
        this.tasksByParent.upsert({ id: parentTaskId, ids: updatedIds });
      }
    }

    const pendingScan = this.activeChildScans.get(parentTaskId);
    if (pendingScan) this.fetchTasksByParent(pendingScan.projectId, parentTaskId, true);

    if (!membershipChanged || !this.tasks.hasDataForId(parentTaskId)) return;

    const parentTask = this.tasks.get(parentTaskId)();
    if (!parentTask || typeof parentTask.subtasksCount !== 'number' || !Number.isFinite(parentTask.subtasksCount)) {
      return;
    }

    const subtasksCount = method === 'add' ? parentTask.subtasksCount + 1 : Math.max(0, parentTask.subtasksCount - 1);
    this.tasks.upsert(Object.assign(new Task({}), parentTask, { subtasksCount }));
  }

  private isObjectEmpty(obj: unknown): boolean {
    let filled = false;
    Object.values(obj).forEach(value => {
      if (
        value !== null &&
        value !== undefined &&
        (value?.length > 0 ||
          (typeof value === 'string' && value === '') ||
          (typeof value === 'boolean' && value === true) ||
          (typeof value === 'object' && value instanceof Date))
      )
        filled = true;
    });
    return !filled;
  }

  private ensureTasksPageLoaded(key: PageKey): void {
    const pageKey = this.resolvePageKey(key);
    const hasData = this.projectTaskPages.hasDataForId(pageKey);
    if (hasData || this.inflight.has(pageKey)) return;
    this.inflight.add(pageKey);
    this.fetchTasksPage(key, () => this.inflight.delete(pageKey));
  }

  private buildFiltersHash(filters?: TaskFilters | null): string {
    if (!filters) return '';
    return JSON.stringify({
      text: filters.text ?? '',
      taskStatus: [...(filters.taskStatus ?? [])].sort(),
      taskPriority: [...(filters.taskPriority ?? [])].sort(),
      group: [...(filters.group ?? [])].sort(),
      noGroup: filters.noGroup ?? false,
      user: [...(filters.user ?? [])].sort(),
      noUsers: filters.noUsers ?? false,
      location: [...(filters.location ?? [])].sort(),
      noLocation: filters.noLocation ?? false,
      industry: [...(filters.industry ?? [])].sort(),
      noIndustry: filters.noIndustry ?? false,
      userRole: [...(filters.userRole ?? [])].sort(),
      createdBy: [...(filters.createdBy ?? [])].sort(),
      startDateFrom: filters.startDateFrom ?? null,
      startDateTo: filters.startDateTo ?? null,
      endDateFrom: filters.endDateFrom ?? null,
      endDateTo: filters.endDateTo ?? null,
      createdDateFrom: filters.createdDateFrom ?? null,
      createdDateTo: filters.createdDateTo ?? null,
      editDateFrom: filters.editDateFrom ?? null,
      editDateTo: filters.editDateTo ?? null,
      projectTagIds: [...(filters.projectTagIds ?? [])].sort(),
      taskTypeIds: [...(filters.taskTypeIds ?? [])].sort()
    });
  }

  public lockTask(taskId: string): Observable<ProjectTaskDTO> {
    return this.tasksHttpService.lockTask(taskId).pipe(tap((dto: ProjectTaskDTO) => this.upsertLocalData(dto)));
  }

  public unlockTask(taskId: string): Observable<ProjectTaskDTO> {
    return this.tasksHttpService.unlockTask(taskId).pipe(tap((dto: ProjectTaskDTO) => this.upsertLocalData(dto)));
  }
}
